import {
  WSAlert,
  WSCard,
  WSElement,
  WSGrid,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import classNames from "classnames";
import { useMemo } from "react";
import { queriesInternalAccounts } from "@query";
import { selectorAccountBalance } from "../../../../shared/selectors/selectorAccountBalance";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorActiveTaxWithholdingAccount } from "../../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { TransferBalance } from "../../types";
import { TransferSchema } from "./TransferSchema";
import styles from "./index.module.scss";

type Props = {
  balance: TransferBalance;
  onDeposit: () => void;
  onWithdrawal: () => void;
};

export const FormType: React.FC<Props> = ({
  balance,
  onDeposit,
  onWithdrawal
}) => {
  const balanceName = useMemo(
    () => (balance === "wallet" ? "Wingspan Wallet" : "Tax Withholdings"),
    [balance]
  );

  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  const activeBankingAccount = useMemo(() => {
    return queryInternalAccounts.data?.data?.find(selectorActiveBankingAccount);
  }, [queryInternalAccounts.data]);

  const activeTaxWithholdingAccount = useMemo(() => {
    return queryInternalAccounts.data?.data?.find(
      selectorActiveTaxWithholdingAccount
    );
  }, [queryInternalAccounts.data]);

  const queryBankingBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: activeBankingAccount?.id! },
      { enabled: !!activeBankingAccount }
    );

  const queryTaxWithholdingBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: activeTaxWithholdingAccount?.id! },
      { enabled: !!activeTaxWithholdingAccount }
    );

  const bankingBalance = queryBankingBalance.data
    ? selectorAccountBalance(queryBankingBalance.data).balance
    : undefined;
  const taxWithholdingsBalance = queryTaxWithholdingBalance.data
    ? selectorAccountBalance(queryTaxWithholdingBalance.data).balance
    : undefined;

  const currency =
    balance === "wallet"
      ? activeBankingAccount?.currency
      : activeTaxWithholdingAccount?.currency;

  const balanceTotal =
    balance === "wallet" ? bankingBalance : taxWithholdingsBalance;

  const sourceAccount =
    balance === "wallet" ? activeBankingAccount : activeTaxWithholdingAccount;

  // There is nothing to withdraw either when the account does not exist yet or
  // when it exists and is empty. Only the second case can quote an amount.
  const hasNoSourceAccount = !queryInternalAccounts.isLoading && !sourceAccount;
  const hasEmptyBalance = balanceTotal !== undefined && balanceTotal <= 0;
  const isWithdrawalDisabled = hasNoSourceAccount || hasEmptyBalance;

  return (
    <WSList gap="2XL">
      <WSElement>
        <WSSectionToolbar title="Where would you like to send your funds?" />
      </WSElement>

      <WSGrid gutter="M">
        <WSGrid.Item span={{ s: "6" }}>
          <WSCard
            header="Transfer to bank"
            onClick={isWithdrawalDisabled ? undefined : onWithdrawal}
            className={classNames({ [styles.disabled]: isWithdrawalDisabled })}
          >
            <WSText.ParagraphSm color="gray500">
              Send funds from your {balanceName} to your personal bank account.
              <br />
              (1 business day)
            </WSText.ParagraphSm>
            <TransferSchema type="withdrawal" mt="XL" />
          </WSCard>

          {hasNoSourceAccount ? (
            <WSAlert theme="info" mt="XL">
              Activate your {balanceName} before transferring funds out of it
            </WSAlert>
          ) : hasEmptyBalance ? (
            <WSAlert theme="info" mt="XL">
              There is no balance (
              {formatMoney(balanceTotal!, currency || "USD")}) to transfer from
              your {balanceName}
            </WSAlert>
          ) : null}
        </WSGrid.Item>
        <WSGrid.Item span={{ s: "6" }}>
          <WSCard header={`Transfer to ${balanceName}`} onClick={onDeposit}>
            <WSText.ParagraphSm color="gray500">
              Send funds from your bank account to your {balanceName}.<br />
              (3-5 business days)
            </WSText.ParagraphSm>

            <TransferSchema type="deposit" mt="XL" />
          </WSCard>
        </WSGrid.Item>
      </WSGrid>
    </WSList>
  );
};
